import isUndefined from 'lodash-es/isUndefined';
import { Observable } from 'rxjs';
import { BrowsingContextError } from '../../browsingContextError';
import { addLinkToRange, findLinkAncestor, findLinkInRange, removeLinksInRange } from '../../helpers/link';
import { createConnectingService } from '../base/connectingService';
import { ElementSelectionService } from '../dom/elementSelectionService';
import { ElementService } from '../dom/elementService';
import { LinkService, NULL_LINK_SERVICE } from '../text/linkService';

export function createHtmlLinkService(
  element$: Observable<Element | null>,
  selectionService: ElementSelectionService,
): LinkService {
  return createConnectingService(
    element$,
    () => NULL_LINK_SERVICE,
    element => new HtmlLinkService(element, selectionService),
  );
}

export class HtmlLinkService extends ElementService<Element> implements LinkService {
  constructor(element: Element, selectionService: ElementSelectionService) {
    super(element, selectionService);

    this.selectionService = selectionService;
  }

  private readonly selectionService: ElementSelectionService;

  get selectedText() {
    const { selection } = this.selectionService;
    if (!selection) {
      return '';
    }

    const { range } = selection;
    return range.toString();
  }

  get link(): HTMLAnchorElement | null {
    const { selection } = this.selectionService;
    if (!selection) {
      return null;
    }

    const { element } = this;
    const { range } = selection;
    return findLinkAncestor(range.commonAncestorContainer, element) || findLinkInRange(element, range);
  }

  get url() {
    const { link } = this;
    return link ? link.getAttribute('href') || '' : undefined;
  }

  setUrl(url: string | undefined) {
    const { selection } = this.selectionService;
    if (!selection) {
      return;
    }

    const { element } = this;
    const { range, isBackward } = selection;
    let newRange = range;

    if (isUndefined(url)) {
      newRange = removeLinksInRange(element, newRange);
    } else {
      const linkAncestor = findLinkAncestor(range.commonAncestorContainer, element);
      const link = linkAncestor || findLinkInRange(element, range);

      if (link) {
        if (link !== linkAncestor) {
          newRange = newRange.cloneRange();
          newRange.selectNodeContents(link);
        }

        decorateLink(link, url);
      } else {
        if (newRange.collapsed) {
          newRange = insertAndSelectTextNode(element, newRange, url);
        }

        newRange = addLinkToRange(element, newRange, newLink => decorateLink(newLink, url));
      }
    }

    if (newRange !== range) {
      this.selectionService.selectWithRange(newRange, isBackward);
    }
  }
}

export function decorateLink(link: HTMLAnchorElement, url: string) {
  link.setAttribute('href', url);
  link.setAttribute('rel', 'noopener noreferrer');
}

function insertAndSelectTextNode(element: Element, range: Range, text: string): Range {
  const document = element.ownerDocument;
  if (!document) {
    throw new BrowsingContextError();
  }

  const textNode = document.createTextNode(text);

  const newRange = range.cloneRange();
  newRange.insertNode(textNode);
  newRange.selectNodeContents(textNode);

  return newRange;
}
